import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { authApi } from '@/services/authApi';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import { PATHS } from '@/constants/paths';
import M_LanguageSwitcher from '@/components/M_Molecules/M_LanguageSwitcher/M_LanguageSwitcher';
import A_Input from '@/components/A_Atoms/A_Input/A_Input';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import A_Notification from '@/components/A_Atoms/A_Notification/A_Notification';
import styles from './AuthPage.module.css';
import cn from 'classnames';
import AuthImage from '@/assets/images/A_ScreenSaver.png';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language, key);
  const navigate = useNavigate();

  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const verifiedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage(getTranslation(language, 'auth.verifyEmailNoToken'));
      return;
    }
    if (verifiedTokenRef.current === token) {
      return;
    }
    verifiedTokenRef.current = token;

    let cancelled = false;
    setStatus('loading');
    authApi
      .verifyEmail(token)
      .then(res => {
        if (cancelled) return;
        if (res.success) {
          setStatus('success');
          setMessage(
            res.data?.detail ||
              getTranslation(language, 'auth.verifyEmailSuccess')
          );
          setTimeout(() => navigate('/'), 2000);
        } else {
          setStatus('error');
          setMessage(
            res.message || getTranslation(language, 'auth.verifyEmailError')
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error');
          setMessage(getTranslation(language, 'auth.verifyEmailError'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, navigate, language]);

  const startCooldown = () => {
    setCooldown(60);
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          cooldownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail.trim() || cooldown > 0) return;
    setResendLoading(true);
    setResendSuccess(false);
    try {
      const res = await authApi.resendVerifyEmail(resendEmail.trim());
      setResendSuccess(res.success);
      setMessage(
        res.success
          ? res.data?.detail || t('auth.resendVerificationSuccess')
          : res.message || ''
      );
      startCooldown();
    } catch {
      setMessage(t('auth.verifyEmailError'));
      startCooldown();
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.action_wrapper}>
        <M_LanguageSwitcher />
        <div className={styles.sign_section}>
          <div className={styles.title_block}>
            <h1>{t('auth.verifyEmailTitle')}</h1>
            {status === 'loading' && (
              <p className='text-p-6'>Проверка ссылки…</p>
            )}
          </div>
          <div className={styles.form_section}>
            {status === 'success' && message && (
              <>
                <A_Notification
                  type='success'
                  message={message}
                  isVisible
                  onClose={() => setMessage(null)}
                />
                <Link
                  to={PATHS.AUTH}
                  className={cn(styles.link_muted, 'text-p-5')}
                >
                  {t('auth.backToLogin')}
                </Link>
              </>
            )}
            {status === 'error' && (
              <>
                {message && (
                  <A_Notification
                    type='error'
                    message={message}
                    isVisible
                    onClose={() => setMessage(null)}
                  />
                )}
                <form onSubmit={handleResend} className={styles.form_inner}>
                  <div className={styles.inputs_row}>
                    <div className={styles.inputs_group}>
                      <A_Input
                        label={t('auth.email')}
                        type='email'
                        value={resendEmail}
                        onChange={setResendEmail}
                        required
                      />
                    </div>
                    <Link
                      to={PATHS.AUTH}
                      className={cn(styles.link_muted, 'text-p-5')}
                    >
                      {t('auth.backToLogin')}
                    </Link>
                  </div>
                  <div className={styles.button_row}>
                    <A_Button
                      type='submit'
                      variant='primary'
                      size='large'
                      disabled={resendLoading || cooldown > 0}
                    >
                      {cooldown > 0
                        ? `${t('auth.resendVerification')} (${cooldown})`
                        : t('auth.resendVerification')}
                    </A_Button>
                  </div>
                </form>
                {resendSuccess && (
                  <p className='text-p-6'>
                    {t('auth.resendVerificationSuccess')}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <div className={styles.image_wrapper}>
        <div className={styles.round_decorator} />
        <img
          src={AuthImage}
          alt='Карусель с фотографиями'
          className={styles.auth_image}
        />
        <span className={cn(styles.copyright_text, 'text-p-6')}>
          2026 © Timecaps
        </span>
      </div>
    </div>
  );
}
